import { cloneCameraPath, type CameraPath } from "@/lib/location-scene-3d/camera-path"
import { cloneObjectPath } from "@/lib/location-scene-3d/object-path"
import type { CameraPathHistoryPatch } from "@/lib/location-scene-3d/camera-path-takes"
import type { SceneCell, SceneOpening, ScenePlatform } from "@/lib/location-scene-3d/platform"
import type { SceneFaceState, SceneLightState, SceneObjectState } from "@/lib/location-scene-3d/scene-state"

export type HistorySnapshot = {
  platform: ScenePlatform | null
  cells: SceneCell[]
  openings: SceneOpening[]
  light: SceneLightState | null
  objects: SceneObjectState[]
  camera_path: CameraPath
  /** Дубль, чья линия в снимке. В старых снимках поля нет. */
  active_camera_path_id?: string | null
  /** Имя на момент снимка. Отмена его не возвращает. */
  active_camera_path_name?: string | null
  /** Прежняя линия этого дубля. Есть только у шага правки линии. */
  prior_camera_path?: CameraPath
  faces?: {
    face_key: string
    origin?: "upload" | "library" | "generated"
    status?: "pending" | "ready" | "failed"
    url?: string | null
    thumb_url?: string | null
    download_url?: string | null
    prompt?: string | null
    solid_color?: string | null
    tile_colors?: Record<string, string> | null
  }[]
}

export type HistoryEntry = {
  id: string
  at: number
  labelKey: string
  labelParams?: Record<string, string | number>
  snapshot: HistorySnapshot
}

const MAX_ENTRIES = 40
const MAX_BYTES = 1_500_000
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const KEY_PREFIX = "frameriver:scene3d-history:v3:"
const PERSIST_DEBOUNCE_MS = 250

/** In-memory stacks — avoid sync localStorage round-trip on every push. */
const memoryStacks = new Map<string, HistoryEntry[]>()
const pendingPersistTimers = new Map<string, ReturnType<typeof setTimeout>>()
let unloadFlushBound = false

/** locationId null → "none" so unlocated scenes do not share one stack. */
export function historyStorageKey(projectId: number, locationId: number | null, sceneKey: string): string {
  const locationKey = locationId == null ? "none" : String(locationId)
  return `${KEY_PREFIX}${projectId}:${locationKey}:${sceneKey}`
}

function trimByAge(entries: HistoryEntry[], now = Date.now()): HistoryEntry[] {
  return entries.filter((entry) => now - entry.at <= MAX_AGE_MS)
}

function trimByCount(entries: HistoryEntry[]): HistoryEntry[] {
  if (entries.length <= MAX_ENTRIES) return entries
  return entries.slice(0, MAX_ENTRIES)
}

function trimByBytes(entries: HistoryEntry[]): HistoryEntry[] {
  let next = entries
  while (next.length > 1) {
    const encoded = JSON.stringify(next)
    if (encoded.length <= MAX_BYTES) break
    next = next.slice(0, -1)
  }
  return next
}

function sanitize(entries: HistoryEntry[]): HistoryEntry[] {
  return trimByBytes(trimByCount(trimByAge(entries)))
}

function bindUnloadFlush() {
  if (unloadFlushBound || typeof window === "undefined") return
  unloadFlushBound = true
  const flush = () => flushActionHistoryPersists()
  window.addEventListener("pagehide", flush)
  window.addEventListener("beforeunload", flush)
}

function persistKeyNow(key: string) {
  const timer = pendingPersistTimers.get(key)
  if (timer != null) {
    clearTimeout(timer)
    pendingPersistTimers.delete(key)
  }
  const entries = memoryStacks.get(key)
  if (entries == null) return
  writeToStorage(key, entries)
}

function schedulePersist(key: string) {
  bindUnloadFlush()
  const existing = pendingPersistTimers.get(key)
  if (existing != null) clearTimeout(existing)
  pendingPersistTimers.set(
    key,
    setTimeout(() => {
      pendingPersistTimers.delete(key)
      const entries = memoryStacks.get(key)
      if (entries == null) return
      writeToStorage(key, entries)
    }, PERSIST_DEBOUNCE_MS),
  )
}

function writeToStorage(key: string, entries: HistoryEntry[]) {
  try {
    const storage = globalThis.localStorage
    if (!storage) return
    storage.setItem(key, JSON.stringify(sanitize(entries)))
  } catch {
    // quota / private mode — drop oldest and retry once
    try {
      const storage = globalThis.localStorage
      if (!storage) return
      const trimmed = sanitize(entries).slice(0, Math.max(5, Math.floor(MAX_ENTRIES / 2)))
      storage.setItem(key, JSON.stringify(trimmed))
    } catch {
      // ignore
    }
  }
}

function readFromStorage(key: string): HistoryEntry[] {
  try {
    const storage = globalThis.localStorage
    if (!storage) return []
    const raw = storage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    const entries: HistoryEntry[] = []
    for (const item of parsed) {
      if (!item || typeof item !== "object") continue
      const row = item as Partial<HistoryEntry>
      if (typeof row.id !== "string" || typeof row.at !== "number" || typeof row.labelKey !== "string") continue
      if (!row.snapshot || typeof row.snapshot !== "object") continue
      entries.push({
        id: row.id,
        at: row.at,
        labelKey: row.labelKey,
        labelParams: row.labelParams,
        snapshot: row.snapshot as HistorySnapshot,
      })
    }
    return sanitize(entries)
  } catch {
    return []
  }
}

/** Flush all pending debounced writes (tests + pagehide). */
export function flushActionHistoryPersists(): void {
  const keys = [...pendingPersistTimers.keys()]
  for (const key of keys) persistKeyNow(key)
}

/** Clear in-memory stacks and pending timers (tests). */
export function resetActionHistoryMemory(): void {
  for (const timer of pendingPersistTimers.values()) clearTimeout(timer)
  pendingPersistTimers.clear()
  memoryStacks.clear()
}

export function loadActionHistory(key: string): HistoryEntry[] {
  const cached = memoryStacks.get(key)
  if (cached) return cached
  const loaded = readFromStorage(key)
  memoryStacks.set(key, loaded)
  return loaded
}

export function saveActionHistory(key: string, entries: HistoryEntry[]): void {
  const next = sanitize(entries)
  memoryStacks.set(key, next)
  schedulePersist(key)
}

export function clearActionHistory(key: string): void {
  memoryStacks.set(key, [])
  const timer = pendingPersistTimers.get(key)
  if (timer != null) {
    clearTimeout(timer)
    pendingPersistTimers.delete(key)
  }
  try {
    globalThis.localStorage?.removeItem(key)
  } catch {
    // ignore
  }
}

/** Move history from one storage key to another (e.g. draft → real previz id). */
export function migrateActionHistory(fromKey: string, toKey: string): HistoryEntry[] {
  if (fromKey === toKey) return loadActionHistory(toKey)
  const from = loadActionHistory(fromKey)
  if (from.length > 0) {
    memoryStacks.set(toKey, sanitize(from))
    schedulePersist(toKey)
  }
  clearActionHistory(fromKey)
  return loadActionHistory(toKey)
}

/** Прежние линии пропущенных шагов (от новых к старым), затем линия выбранной строки. */
export function historyJumpCameraPatches(
  target: HistorySnapshot,
  skippedNewestFirst: readonly HistorySnapshot[],
): CameraPathHistoryPatch[] {
  const patches: CameraPathHistoryPatch[] = []
  for (const snap of skippedNewestFirst) {
    if (!snap.prior_camera_path) continue
    patches.push({
      takeId: snap.active_camera_path_id ?? null,
      path: snap.prior_camera_path,
    })
  }
  patches.push({
    takeId: target.active_camera_path_id ?? null,
    path: target.camera_path,
  })
  return patches
}

export type HistoryRewind = {
  entries: HistoryEntry[]
  redo: HistoryEntry[]
}

/**
 * Jump to an entry. Stack keeps that entry and older ones.
 * Newer entries become redo, nearest first. Clicking the newest clears redo.
 */
export function rewindActionHistory(key: string, entryId: string): HistoryRewind {
  const entries = loadActionHistory(key)
  const index = entries.findIndex((entry) => entry.id === entryId)
  if (index <= 0) return { entries, redo: [] }
  const remaining = entries.slice(index)
  memoryStacks.set(key, remaining)
  schedulePersist(key)
  return { entries: remaining, redo: entries.slice(0, index).reverse() }
}

/** Drop newest entry; returns remaining stack (newest-first). */
export function popNewestActionHistory(key: string): HistoryEntry[] {
  const prev = loadActionHistory(key)
  const next = prev.slice(1)
  memoryStacks.set(key, next)
  schedulePersist(key)
  return next
}

export function pushActionHistory(
  key: string,
  entry: Omit<HistoryEntry, "id" | "at"> & { id?: string; at?: number },
): HistoryEntry[] {
  const nextEntry: HistoryEntry = {
    id: entry.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    at: entry.at ?? Date.now(),
    labelKey: entry.labelKey,
    labelParams: entry.labelParams,
    snapshot: entry.snapshot,
  }
  const prev = loadActionHistory(key)
  const next = sanitize([nextEntry, ...prev])
  memoryStacks.set(key, next)
  schedulePersist(key)
  return next
}

export function snapshotFromVisual(args: {
  platform: ScenePlatform | null
  cells: SceneCell[]
  openings: SceneOpening[]
  light: SceneLightState | null
  objects: SceneObjectState[]
  camera_path: CameraPath
  faces?: HistorySnapshot["faces"]
}): HistorySnapshot {
  return {
    platform: args.platform,
    cells: args.cells.map((cell) => ({ ...cell })),
    openings: args.openings.map((opening) => ({ ...opening })),
    light: args.light ? { ...args.light, position: [...args.light.position] as [number, number, number] } : null,
    objects: args.objects.map((obj) => ({
      ...obj,
      position: [...obj.position] as [number, number, number],
      rotation: [...obj.rotation] as [number, number, number],
      scale: [...obj.scale] as [number, number, number],
      size: obj.size ? ([...obj.size] as [number, number, number]) : undefined,
      ...(obj.path ? { path: cloneObjectPath(obj.path) } : {}),
    })),
    camera_path: cloneCameraPath(args.camera_path),
    faces: args.faces?.map((face) => ({ ...face })),
  }
}

export function facesFromHistorySnapshot(faces: HistorySnapshot["faces"] | undefined): SceneFaceState[] {
  return (faces ?? []).map((face) => ({
    face_key: face.face_key,
    origin: face.origin ?? "upload",
    status: face.status ?? "ready",
    url: face.url,
    thumb_url: face.thumb_url,
    download_url: face.download_url,
    prompt: face.prompt,
    solid_color: face.solid_color,
    tile_colors: face.tile_colors ? { ...face.tile_colors } : face.tile_colors,
  }))
}
